import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
 surface:'#1A1D27', surface2:'#22263A',
 border:'rgba(255,255,255,.08)', border2:'rgba(255,255,255,.14)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 accent:'#3AAF58',
 red:'#E05050', redBg:'rgba(224,80,80,.15)',
 amber:'#D4840F', amberBg:'rgba(212,132,15,.15)',
 green:'#3AAF58', greenBg:'rgba(58,175,88,.15)',
 blue:'#2563A8', blueBg:'rgba(37,99,168,.15)',
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const STATUS_STYLE = {
 'Confirmed': { bg:'rgba(224,80,80,.2)', border:'rgba(224,80,80,.5)', color:'#E05050', dot:'#E05050' },
 'Tentative': { bg:'rgba(212,132,15,.2)', border:'rgba(212,132,15,.5)', color:'#D4840F', dot:'#D4840F' },
 'Checked In': { bg:'rgba(37,99,168,.2)', border:'rgba(37,99,168,.5)', color:'#3B82F6', dot:'#3B82F6' },
 'Checked Out': { bg:'rgba(160,168,192,.1)',border:'rgba(160,168,192,.3)',color:'#A0A8C0', dot:'#A0A8C0' },
 'Cancelled': { bg:'rgba(96,104,128,.1)', border:'rgba(96,104,128,.2)', color:'#606880', dot:'#606880' },
};

export default function AvailabilityCalendar({ onDateSelect, selectedDate }) {
 const [bookings, setBookings] = useState([]);
 const [loading, setLoading] = useState(true);
 const today = new Date();
 const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
 const [hoveredDate, setHoveredDate] = useState(null);
 const [tooltip, setTooltip] = useState(null);

 const load = useCallback(async () => {
 try {
 const r = await fetch(`${API}/bookings`);
 const d = await r.json();
 setBookings(Array.isArray(d) ? d : []);
 } catch {}
 finally { setLoading(false); }
 }, []);

 useEffect(() => { load(); }, [load]);

 const month = viewDate.getMonth();
 const year = viewDate.getFullYear();
 const firstDay = new Date(year, month, 1).getDay();
 const daysInMonth = new Date(year, month + 1, 0).getDate();

 const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
 const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

 const getBookingsForDate = (date) => {
 const d = `${year}-${String(month+1).padStart(2,'0')}-${String(date).padStart(2,'0')}`;
 return bookings.filter(b => {
 const start = b.start_date?.slice(0,10);
 const end = b.end_date?.slice(0,10);
 return d >= start && d <= end;
 });
 };

 const formatDate = (date) => `${year}-${String(month+1).padStart(2,'0')}-${String(date).padStart(2,'0')}`;
 const isToday = (date) => {
 return date === today.getDate() && month === today.getMonth() && year === today.getFullYear();
 };
 const isSelected = (date) => selectedDate === formatDate(date);

 const totalBooked = bookings.filter(b => {
 const bMonth = new Date(b.start_date).getMonth();
 const bYear = new Date(b.start_date).getFullYear();
 return bMonth === month && bYear === year && b.booking_status !== 'Cancelled';
 }).length;

 return (
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:16, marginBottom:20 }}>
 {/* Header */}
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
 <div>
 <div style={{ fontWeight:800, fontSize:15, color:C.text }}> Availability Calendar</div>
 <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{totalBooked} bookings in {MONTHS[month]} {year}</div>
 </div>
 <div style={{ display:'flex', gap:8, alignItems:'center' }}>
 <button onClick={prevMonth} style={{ padding:'5px 10px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:6, color:C.text2, cursor:'pointer', fontSize:13 }}></button>
 <div style={{ fontWeight:700, fontSize:14, color:C.text, minWidth:140, textAlign:'center' }}>{MONTHS[month]} {year}</div>
 <button onClick={nextMonth} style={{ padding:'5px 10px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:6, color:C.text2, cursor:'pointer', fontSize:13 }}></button>
 <button onClick={()=>setViewDate(new Date(today.getFullYear(),today.getMonth(),1))}
 style={{ padding:'5px 10px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:6, color:C.accent, cursor:'pointer', fontSize:12, fontWeight:600 }}>Today</button>
 <button onClick={load} style={{ padding:'5px 10px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:6, color:C.text2, cursor:'pointer', fontSize:12 }}></button>
 </div>
 </div>

 {/* Legend */}
 <div style={{ display:'flex', gap:14, marginBottom:12, flexWrap:'wrap' }}>
 {[['Confirmed','#E05050'],['Tentative','#D4840F'],['Checked In','#3B82F6'],['Available','#3AAF58']].map(([label,color])=>(
 <div key={label} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:C.text2 }}>
 <div style={{ width:10, height:10, borderRadius:3, background:color, opacity:.8 }} />
 {label}
 </div>
 ))}
 </div>

 {/* Day headers */}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:3 }}>
 {DAYS.map(d => (
 <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:700, color:C.text3, padding:'4px 0' }}>{d}</div>
 ))}
 </div>

 {/* Calendar grid */}
 {loading ? (
 <div style={{ textAlign:'center', padding:30, color:C.text3 }}>Loading bookings…</div>
 ) : (
 <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3 }}>
 {/* Empty cells */}
 {Array.from({length:firstDay},(_,i) => (
 <div key={`empty-${i}`} style={{ height:64 }} />
 ))}

 {/* Days */}
 {Array.from({length:daysInMonth},(_,i) => {
 const date = i + 1;
 const dayBookings = getBookingsForDate(date);
 const isPast = new Date(year, month, date) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
 const isSel = isSelected(date);

 return (
 <div key={date}
 onClick={() => { if (onDateSelect) onDateSelect(formatDate(date)); }}
 onMouseEnter={() => dayBookings.length > 0 && setTooltip({date, bookings:dayBookings})}
 onMouseLeave={() => setTooltip(null)}
 style={{
 height:64, padding:'4px 5px', borderRadius:8, cursor:'pointer', position:'relative',
 background: isSel ? `${C.accent}30` : isToday(date) ? 'rgba(58,175,88,.08)' : C.surface2,
 border: `1px solid ${isSel ? C.accent : isToday(date) ? `${C.accent}50` : C.border}`,
 opacity: isPast ? 0.5 : 1,
 transition:'all .15s',
 }}>
 <div style={{ fontSize:12, fontWeight:isToday(date)?800:600, color:isToday(date)?C.accent:isSel?C.accent:C.text, marginBottom:3 }}>{date}</div>
 {/* Booking dots */}
 <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
 {dayBookings.slice(0,3).map((b,bi) => {
 const st = STATUS_STYLE[b.booking_status] || STATUS_STYLE['Tentative'];
 return (
 <div key={bi} style={{ fontSize:9, fontWeight:600, color:st.color, background:st.bg, borderRadius:3, padding:'1px 4px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.4 }}>
 {b.guest_name || b.company_name || '—'}
 </div>
 );
 })}
 {dayBookings.length > 3 && <div style={{ fontSize:9, color:C.text3 }}>+{dayBookings.length-3} more</div>}
 </div>
 </div>
 );
 })}
 </div>
 )}

 {/* Tooltip */}
 {tooltip && (
 <div style={{ marginTop:12, background:C.surface2, borderRadius:10, padding:12, border:`1px solid ${C.border2}` }}>
 <div style={{ fontWeight:700, fontSize:13, color:C.text, marginBottom:8 }}>
 {tooltip.date} {MONTHS[month]} — {tooltip.bookings.length} booking(s)
 </div>
 {tooltip.bookings.map((b,i) => {
 const st = STATUS_STYLE[b.booking_status] || STATUS_STYLE['Tentative'];
 return (
 <div key={i} style={{ display:'flex', gap:10, alignItems:'center', padding:'6px 0', borderBottom:`1px solid ${C.border}` }}>
 <div style={{ width:8, height:8, borderRadius:'50%', background:st.dot, flexShrink:0 }} />
 <div style={{ flex:1 }}>
 <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{b.guest_name || b.company_name || '—'}</div>
 <div style={{ fontSize:11, color:C.text3 }}>{b.booking_type} · {b.pax_count} pax</div>
 </div>
 <div style={{ textAlign:'right' }}>
 <div style={{ fontSize:11, fontWeight:700, color:st.color }}>{b.booking_status}</div>
 <div style={{ fontSize:11, color:C.text3 }}>{b.salesperson || '—'}</div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 );
}
